import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, ShoppingCart, Clock, CheckCircle2 } from "lucide-react";
import SellerLayout, { useSellerSession } from "./layout";
import type { Client } from "@shared/schema";

interface OrderItem {
  id: string;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  product: { name: string };
}

interface OrderWithItems {
  id: string;
  clientId: string;
  status: string;
  orderNumber: number;
  totalAmount: string | null;
  createdAt: string;
  client: Client;
  items: OrderItem[];
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Ny",
  confirmed: "Bekräftad",
  in_production: "I produktion",
  packed: "Packad",
  delivered: "Levererad",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  in_production: "bg-purple-50 text-purple-700 border-purple-200",
  packed: "bg-orange-50 text-orange-700 border-orange-200",
  delivered: "bg-green-50 text-green-700 border-green-200",
};

export default function SellerDashboard() {
  const { data: seller } = useSellerSession();
  const { data: clients, isLoading: clientsLoading } = useQuery<Client[]>({ queryKey: ["/api/seller/clients"] });
  const { data: todayOrders, isLoading: ordersLoading } = useQuery<OrderWithItems[]>({
    queryKey: ["/api/seller/orders/today"],
    enabled: seller != null && seller.canViewOrders === true,
  });

  return (
    <SellerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Välkommen, {seller?.name}</h1>
          <p className="text-muted-foreground">Här är en översikt över dina kunder och dagens ordrar.</p>
        </div>

        {/* Stats */}
        <div className={`grid grid-cols-1 gap-4 ${seller?.canViewOrders ? "sm:grid-cols-3" : "sm:grid-cols-1 max-w-xs"}`}>
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-full bg-blue-100 p-3">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Mina kunder</p>
                {clientsLoading ? (
                  <Skeleton className="h-7 w-10 mt-1" />
                ) : (
                  <p className="text-2xl font-bold" data-testid="stat-my-clients">{clients?.length ?? 0}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {seller?.canViewOrders && (
            <>
              <Card>
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="rounded-full bg-green-100 p-3">
                    <ShoppingCart className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Ordrar idag</p>
                    {ordersLoading ? (
                      <Skeleton className="h-7 w-10 mt-1" />
                    ) : (
                      <p className="text-2xl font-bold" data-testid="stat-today-orders">{todayOrders?.length ?? 0}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="rounded-full bg-yellow-100 p-3">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Väntande</p>
                    {ordersLoading ? (
                      <Skeleton className="h-7 w-10 mt-1" />
                    ) : (
                      <p className="text-2xl font-bold" data-testid="stat-pending-orders">
                        {todayOrders?.filter(o => o.status === "pending").length ?? 0}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Today's orders table */}
        {seller?.canViewOrders && (
          <Card>
            <CardHeader>
              <CardTitle>Dagens ordrar</CardTitle>
            </CardHeader>
            <CardContent>
              {ordersLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : todayOrders && todayOrders.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order #</TableHead>
                        <TableHead>Kund</TableHead>
                        <TableHead>Artiklar</TableHead>
                        <TableHead className="text-right">Totalt</TableHead>
                        <TableHead className="text-right">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {todayOrders.map(order => (
                        <TableRow key={order.id} data-testid={`row-order-${order.id}`}>
                          <TableCell className="font-medium">#{order.orderNumber}</TableCell>
                          <TableCell data-testid={`order-client-${order.id}`}>{order.client?.name ?? "—"}</TableCell>
                          <TableCell>
                            <div className="space-y-0.5" data-testid={`order-products-${order.id}`}>
                              {order.items?.length > 0 ? order.items.map((item, idx) => (
                                <p key={idx} className="text-xs text-muted-foreground">
                                  {item.quantity} × {item.product.name}
                                </p>
                              )) : <span className="text-xs text-muted-foreground">—</span>}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium" data-testid={`order-total-${order.id}`}>
                            {order.totalAmount ? `${parseFloat(order.totalAmount).toFixed(2)} kr` : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline" className={`text-xs ${STATUS_COLORS[order.status] ?? ""}`}>
                              {STATUS_LABELS[order.status] ?? order.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">Inga ordrar idag ännu</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </SellerLayout>
  );
}
